import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Loading } from "../components/Loading";
import { PayoutDetailsForm } from "../components/PayoutDetailsForm";
import { IconCard, IconDoc, EditDocumentIcon, IconInfo, IconLock, IconLogout, IconShield, IconUser } from "../components/Icons";
import { payouts as payoutsApi } from "../api/endpoints";
import { useAuth } from "../store/auth";
import { useConfig } from "../store/config";
import { useToast } from "../hooks/useToast";
import type { PayoutDetailsState } from "../types/api";

function countryName(code: string | null | undefined, countries: Array<{ code: string; name: string }>) {
  if (!code) return "—";
  return countries.find((entry) => entry.code === code)?.name ?? code;
}

function genderLabel(value?: string | null) {
  if (value === "female") return "Woman";
  if (value === "male") return "Man";
  if (!value) return "—";
  return value;
}

export function Account() {
  const contributor = useAuth((state) => state.contributor);
  const logout = useAuth((state) => state.logout);
  const countries = useConfig((state) => state.config.countries);
  const toast = useToast();
  const [details, setDetails] = useState<PayoutDetailsState | null>(null);
  const [editingPay, setEditingPay] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    payoutsApi
      .eligibility()
      .then((result) => {
        if (!cancelled) setDetails(result.payout_details ?? null);
      })
      .catch(() => {
        if (!cancelled) setDetails(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!contributor) return null;
  if (loading) return <Layout title="Account"><Loading label="Loading your account…" /></Layout>;

  const bank = details?.details;
  const rate = (contributor.rate_cents / 100).toFixed(2);

  return (
    <Layout title="Account">
      <section className="page">
        <div className="page-kicker">ACCOUNT</div>
        <h1>Account</h1>
        <div className="account-grid">
          <div className="side-stack">
            <article className="soft-card account-card contribution-details-card">
              <h2>
                <span className="icon-tile"><IconUser width={16} height={16} /></span>
                Personal information
              </h2>
              <div className="kv"><span>Full name</span><b>{contributor.name}</b></div>
              <div className="kv"><span>Email</span><b>{contributor.email}</b></div>
              <div className="kv"><span>WhatsApp</span><b>{contributor.phone || "—"}</b></div>
              <div className="kv"><span>Gender</span><b>{genderLabel(contributor.gender)}</b></div>
              <div className="kv">
                <span>Country</span>
                <b>{countryName(contributor.country, countries)}</b>
              </div>
              <div className="notice-amber" style={{ marginTop: 12 }}>
                <IconInfo width={14} height={14} /> Your contributor country can't be changed because it determines your contribution rate.
              </div>
            </article>

            <article className="soft-card account-card payment-details-card">
              <h2>
                <span className="icon-tile"><IconShield width={16} height={16} /></span>
                Contribution details
              </h2>
              <div className="kv"><span>Contributor status</span><b style={{ color: "var(--ok)" }}>Active</b></div>
              <div className="kv"><span>Country</span><b>{countryName(contributor.country, countries)}</b></div>
              <div className="kv">
                <span>Current rate</span>
                <b className="accent-text">${rate} per approved voice note</b>
              </div>
            </article>

            <article className="soft-card account-card legal-details-card">
              <h2>
                <span className="icon-tile"><IconDoc width={16} height={16} /></span>
                Legal
              </h2>
              <div className="kv"><span>Contributor Terms</span><Link to="/terms">View</Link></div>
              <div className="kv privacy-link"><span>Privacy Policy</span><Link to="/privacy">View</Link></div>
            </article>
          </div>

          <div className="side-stack">
            <article className="soft-card account-card payment-details-side">
              <h2>
                <span className="icon-tile"><IconCard width={16} height={16} /></span>
                Payment details
                <button type="button" className="icon-btn" aria-label="Edit payment details" onClick={() => setEditingPay(true)}>
                  <EditDocumentIcon />
                </button>
              </h2>
              <div className="kv"><span>Full legal name</span><b>{bank?.full_legal_name || "—"}</b></div>
              <div className="kv"><span>PayPal/Wise account</span><b>{bank?.email || contributor.paypal_email || "—"}</b></div>
              <div className="kv"><span>Bank country</span><b>{bank?.country_name || bank?.country || "—"}</b></div>
              <div className="kv"><span>Bank name</span><b>{bank?.bank_name || "—"}</b></div>
              <div className="kv"><span>Acc number/IBAN</span><b>{bank ? `····${bank.account_number_last4}` : "—"}</b></div>
              <div className="kv"><span>Currency</span><b>{bank?.currency || "—"}</b></div>
              <div className="kv"><span>Address</span><b>{bank?.beneficiary_address || "—"}</b></div>
            </article>

            <article className="soft-card account-card">
              <h2>
                <span className="icon-tile"><IconLock width={16} height={16} /></span>
                Account
              </h2>
              <div className="kv"><span>Help & Support</span><Link to="/contact">Open</Link></div>
              <div className="kv"><span>Contact Inputry</span><Link to="/contact">Open</Link></div>
              <div className="kv">
                <button
                  type="button"
                  className="signout"
                  style={{ color: "var(--danger)", padding: 0 }}
                  onClick={() => void logout().then(() => window.location.assign("/login"))}
                >
                  Sign out <IconLogout />
                </button>
              </div>
            </article>
            <article className="soft-card account-card signout-card">
              <div className="kv">
                  <button
                    type="button"
                    className="signout"
                    onClick={() => void logout().then(() => window.location.assign("/login"))}
                  >
                    Sign out <IconLogout />
                  </button>
                </div>
            </article>
          </div>
        </div>
      </section>

      {editingPay && (
        <div className="modal-back" role="dialog" aria-label="Payment details">
          <div className="modal-card">
            <h2 style={{ marginTop: 0 }}>Payment details</h2>
            <PayoutDetailsForm
              existing={bank}
              defaultCountry={contributor.country}
              submitLabel="Save"
              onSaved={() => {
                setEditingPay(false);
                toast.show("Payment details saved.", "success");
                void payoutsApi.eligibility().then((result) => setDetails(result.payout_details ?? null));
              }}
              secondary={
                <button type="button" className="btn btn-ghost" onClick={() => setEditingPay(false)}>
                  Cancel
                </button>
              }
            />
          </div>
        </div>
      )}
    </Layout>
  );
}
